import {Button, ScrollArea, Table, Text, createStyles} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  hasPreviewSequenceText,
  PreviewSequenceNameCell,
  resolvePreviewSequenceItems,
  type PreviewSequenceDisplayItem,
} from './predictionInputSequences';
import type {PredictionJobComponent} from './PredictionJobTypes';

const WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

interface StructureResultsPreviewProps {
  components: ReadonlyArray<PredictionJobComponent>;
  configJson?: Record<string, unknown> | null;
  downloadUrl: string;
  model: ScientificModelNode;
  onRefreshDownloadUrls?: () => void;
  opened: boolean;
}

export default function StructureResultsPreview({
  components,
  configJson,
  downloadUrl,
  model,
  onRefreshDownloadUrls,
  opened,
}: StructureResultsPreviewProps): JSX.Element | null {
  const {classes} = useStyles();
  const [sequenceItems, setSequenceItems] = useState<Array<PreviewSequenceDisplayItem>>([]);
  const [sequencesLoading, setSequencesLoading] = useState(false);

  useEffect(() => {
    if (!opened) {
      return;
    }

    let active = true;
    setSequencesLoading(true);
    resolvePreviewSequenceItems(components, model, configJson)
      .then((items) => {
        if (active) {
          setSequenceItems(items);
        }
      })
      .catch(() => {
        if (active) {
          setSequenceItems([]);
        }
      })
      .finally(() => {
        if (active) {
          setSequencesLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [components, configJson, model, opened]);

  const handleDownload = useCallback((): void => {
    if (isS3URLExpired(downloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(downloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  }, [downloadUrl, onRefreshDownloadUrls]);

  const handleViewerInteraction = useCallback((): void => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.INTERACTED_MODEL_HUB_PREVIEW_VIEWER);
  }, []);

  if (!opened) {
    return null;
  }

  const showSequenceColumn = hasPreviewSequenceText(sequenceItems);

  return (
    <>
      <div className={classes.viewerSection}>
        <div className={classes.viewerWrapper}>
          <MolstarViewer
            url={downloadUrl}
            format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER]}
            className={classNames('reset', classes.viewer)}
            interactionAnalyticsCallback={handleViewerInteraction}
          />
        </div>
      </div>
      <div className={classes.sequencesSection}>
        {sequencesLoading ? (
          <CenteredHorizontalThrobber />
        ) : sequenceItems.length === 0 ? (
          <Text size="sm" color="dimmed" ta="center">
            No components configured for this job.
          </Text>
        ) : (
          <div className={classes.sequencesTableWrapper}>
            <ScrollArea className={classes.sequencesScrollArea} type="auto">
              <Table className={classes.sequencesTable} verticalSpacing="sm">
                <thead>
                  <tr>
                    <th className={classes.headerCell}>Component</th>
                    {showSequenceColumn && <th className={classes.headerCell}>Sequence / SMILES</th>}
                  </tr>
                </thead>
                <tbody>
                  {sequenceItems.map((item) => (
                    <tr key={item.key}>
                      <td className={classes.nameColumn}>
                        <PreviewSequenceNameCell item={item} />
                      </td>
                      {showSequenceColumn && <td className={classes.sequenceColumn}>{item.text}</td>}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </div>
      <div className={classes.footer}>
        <Button variant="primary" onClick={handleDownload}>
          Download structure
        </Button>
      </div>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  viewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    height: 'min(560px, 65vh)',
    minHeight: 320,
    maxWidth: '100%',
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  viewer: {
    borderWidth: 0,
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    height: '100%',
  },
  viewerSection: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    width: '100%',
  },
  sequencesSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sequencesTableWrapper: {
    boxSizing: 'border-box',
    width: '100%',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
  },
  sequencesScrollArea: {
    width: '100%',
    maxHeight: 280,
  },
  sequencesTable: {
    width: '100%',
    tableLayout: 'fixed',
    '& tbody tr:not(:last-child) td': {
      borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    },
  },
  headerCell: {
    textAlign: 'left',
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  nameColumn: {
    width: '35%',
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    borderRight: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  sequenceColumn: {
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    marginTop: theme.spacing.lg,
    marginLeft: -theme.spacing.l,
    marginRight: -theme.spacing.l,
    marginBottom: -theme.spacing.l,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    paddingLeft: theme.spacing.l,
    paddingRight: theme.spacing.l,
  },
}));
